import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useBasicInfo } from '@/src/listings/components/_shared/contexts/BasicInfoContext';
import type { LocationProps, RefundPolicyProps } from '@/src/listings/types';
import type { WebappResponse } from '@eventbrite/event-listing-service-client';
import { GoodToKnow as G2K } from '@eventbrite/good-to-know';
import { formatDuration, getDurationObject, formatDoorTime } from '@/src/listings/components/GoodToKnow/utils';
import { getTranslatedRefundPolicy, isNotValidDuration } from '@/src/listings/components/GoodToKnow/utils';
import { ageRestrictionsMapTranslations } from '@/src/listings/components/GoodToKnow/translations';

type G2KHighlights = React.ComponentProps<typeof G2K>['highlights'];

export const useGoodToKnowData = (
  highlights: NonNullable<WebappResponse['goodToKnow']>['highlights'],
  refundPolicy: NonNullable<WebappResponse['goodToKnow']>['refundPolicy'],
) => {
  const { locale, messages, formatMessage } = useIntl();
  const { isSeries, isOnline, venue, isFree, startDate, hideEndDate, hideStartDate } = useBasicInfo();

  const hideStartOrEndDate = hideEndDate || hideStartDate;

  const location: LocationProps = useMemo(
    () =>
      ({
        hasAddress: !!venue,
        isOnlineEvent: isOnline,
      }) as LocationProps,
    [venue, isOnline],
  );

  const parsedRefundPolicy: RefundPolicyProps = useMemo(
    () =>
      ({
        ...refundPolicy,
        isPaidEvent: !isFree,
        isRefundRequestAllowed: refundPolicy?.isRefundAllowed,
        refundPolicyOrganizer: refundPolicy?.refundRequestOrganizer,
      }) as RefundPolicyProps,
    [isFree, refundPolicy],
  );

  const dHighlights: G2KHighlights | undefined = useMemo(() => {
    if (!highlights) return undefined;

    const timezone = startDate?.timezone || 'UTC';
    const formattedDoorTime = highlights.doorTime
      ? formatDoorTime(highlights.doorTime, timezone, locale, messages)
      : undefined;

    const getTranslatedAgeRestriction = (ageRestriction: string | undefined) => {
      if (!ageRestriction) return undefined;

      const messageObject =
        ageRestrictionsMapTranslations[ageRestriction as keyof typeof ageRestrictionsMapTranslations];
      return messageObject ? formatMessage(messageObject) : ageRestriction;
    };

    const translatedAgeRestriction = getTranslatedAgeRestriction(highlights.ageRestriction);

    return {
      ...highlights,
      isOnline,
      doorTime: formattedDoorTime,
      age_restriction: translatedAgeRestriction,
      durationString:
        !highlights.durationInMinutes || isNotValidDuration(highlights.durationInMinutes) || hideStartOrEndDate
          ? undefined
          : formatDuration(getDurationObject(highlights.durationInMinutes), locale, messages),
      parking: highlights.parking === 'free' || highlights.parking === 'paid',
      freeParking: highlights.parking === 'free',
      paidParking: highlights.parking === 'paid',
    };
  }, [highlights, isOnline, locale, messages, startDate, hideStartOrEndDate, formatMessage]);

  const translatedRefundPolicy = getTranslatedRefundPolicy(parsedRefundPolicy, isSeries, locale, messages);

  return {
    dHighlights,
    translatedRefundPolicy,
    location,
  };
};
